import { logError, isEmpty } from "src/utils/helpers"

/**
 * Default properties that should be received with `/api/kiosk/brand/getKioskBrandSettings` endpoint for each feature.
 *
 * @type {object}
 */
const defaultFeatureProperties = {
  // Required main label
  name: "",
  // Smaller text below main label
  description: null,
  // If it's one of our features:
  // ["requestQuote"|"documents"|"reportClaim"|"payments"|"inventory"|"services"]
  // or some unique string if it's for example custom page content
  feature: null,
  // Internal URL
  url: null,
  // If this feature just opens external URL
  externalUrl: null,
  isExternalUrl: false,
  // For setting left icon in main menu list
  iconUrl: null,
  // Backend (`campayn/campayn`) API endpoint that will return custom content when page of this feature is opened,
  // without base URL (e.g. https://pathwayport.com/api/kiosk)
  requestPath: null,
  // Feature type:
  // ["default"|"customPage"|"form"]
  type: 'default',
  // Sort/order, higher the number - lower it is on the list,
  // but if not set, feature will be shown in order in which they came in from backend
  order: 0,
}

/**
 * Some properties will be overwritten form backend ("url" will stay and it should match internal Kiosk routes)
 *
 * // TODO: PAT-3298 Deprecate, because backend should dictate which features show up on kiosk
 *
 * @type {array}
 */
const defaultFeatureSettings = [
  {
    leftIcon: 'GetQuote',
    name: 'Get Quote',
    feature: 'requestQuote',
    description: "Get an instance quote now",
    url: '/quote',
    isExternalUrl: false,
    rightIcon: 'right',
  },
  {
    leftIcon: 'Docs',
    name: 'My Documents',
    feature: 'documents',
    description: "Access your insurance documents",
    url: '/documents',
    isExternalUrl: false,
    rightIcon: 'right',
  },
  {
    leftIcon: 'Claim',
    name: 'Report Claim',
    feature: 'reportClaim',
    description: "Submit an auto claim",
    url: '/claims/form',
    isExternalUrl: false,
    rightIcon: 'right',
  },
  {
    leftIcon: 'Payment',
    name: 'Payments',
    feature: 'payments',
    description: "Send a payment to your broker",
    url: '/payments',
    isExternalUrl: false,
    rightIcon: 'right',
  },
  {
    leftIcon: 'Inventory',
    name: 'Home Inventory',
    feature: 'inventory',
    description: "Organize the value of the personal belongings",
    url: '/inventory',
    isExternalUrl: false,
    rightIcon: 'right',
  },
  {
    leftIcon: 'VehicleGear',
    name: 'Road Services',
    feature: 'services',
    description: "Gas, Locksmiths, Tow Truck, Police",
    url: '/services',
    isExternalUrl: false,
    rightIcon: 'right',
  },
];

/**
 * Extract feature URL
 *
 * @param features Combined features
 * @param feature
 * @return {string|null|*}
 */
export function getInternalFeatureUrl(features, feature) {
  try {
    // Handle Chat since it's not on this list
    if (feature.toLowerCase() === 'chat') {
      return '/chat'
    }

    const findFeature = features.find(f => f.feature.toLowerCase() === feature.toLowerCase())

    if (findFeature && findFeature.hasOwnProperty('isExternalUrl') && findFeature.isExternalUrl === true) {
      return null
    }

    if (findFeature && findFeature.hasOwnProperty('url')) {
      return findFeature.url
    }

    return null
  } catch (e) {
    return null
  }
}

/**
 * Check if feature is selected in Pathway settings and it doesn't have custom URL
 *
 * @param {string} feature Feature to check
 * @param {object} features List of enabled or disable feature, just true/false without details
 * @param {object} labels Details about features, containing Custom URL
 * @return {boolean}
 */
export function isFeatureUsed(feature, features, labels) {
  try {
    // If feature is definitely not enabled
    if (!(feature && features && features[feature] === true)) {
      return false
    }
    // If feature has custom URL - so don't use it for homepage
    return !(
      labels && labels.features && labels.features[feature]
      && labels.features[feature].url && labels.features[feature].url.length > 0
    )
  } catch (e) {
    return false
  }
}

/**
 * Checks if Chat is only enabled feature.
 * Used for hiding bottom navigation, redirecting to /chat from home page, etc.
 *
 * @param {object} settings{} All settings received from backend
 * @param {object} settings{}.features Features selected and saved on Pathway side
 * @return {boolean}
 */
export function checkIfChatOnly(settings) {
  try {
    const { features } = settings
    // Exit if chat is not even enabled
    if(!(features && features.hasOwnProperty('chat') && features.chat === true)) {
      return false
    }

    return isEmpty(getFeaturesAndOptions(settings))
  } catch (e) {
    logError('Check if chat only', e)
    return false
  }
}

/**
 * Check if chat is currently enabled, based on additional chat options (hidden after working hours, etc.),
 * and if it's even enabled of course.
 *
 * @param features
 * @param chatOptions
 * @param isAfterHours
 * @param isChatEmpty
 * @return {boolean}
 */
export function isChatCurrentlyEnabled(features, chatOptions, isAfterHours, isChatEmpty) {
  const defaultResponse = false

  try {
    // Chat not turned on
    if (features.chat !== true) {
      return defaultResponse
    }

    const isHideChatAfterHours = !isEmpty(chatOptions)
      && chatOptions.hasOwnProperty('hideChatAfterHours')
      && chatOptions.hideChatAfterHours === true

    if (isChatEmpty && isAfterHours && isHideChatAfterHours) {
      return defaultResponse
    }

    return true

  } catch (e) {
    logError('Check if chat is currently enabled', e)
    return defaultResponse
  }
}

/**
 * Bottom navigation is only needed if chat is enabled and if it's not the only feature.
 *
 * @param features List of all features with true/false values
 * @param chatOptions Additional Chat options (e.g. hideChatAfterHours)
 * @param isAfterHours Calculated based on working hours
 * @param isChatEmpty If there are no messages in the chat
 * @param isChatOnlyFeature
 * @return {boolean}
 */
export function isNavBottomVisible(features, chatOptions, isAfterHours, isChatEmpty, isChatOnlyFeature) {
  const defaultResponse = false

  try {
    // No need for bottom navigation - if Chat is disabled
    if (!isChatCurrentlyEnabled(features, chatOptions, isAfterHours, isChatEmpty)) {
      return defaultResponse
    }

    // No need for bottom navigation - if Chat is only enabled feature
    if (isChatOnlyFeature) {
      return defaultResponse
    }

    return true
  } catch (e) {
    logError('Check if bottom navigation is needed', e)
    return defaultResponse
  }
}

/**
 * Takes default/fallback features and combines them with features received from PW API
 *
 * // TODO: PAT-3298 Remove if it works properly with new code (getFeaturesLegacy())
 *
 * @deprecated Because this was able to show only features defined here on Kiosk side,
 * but now we're taking features from backend directly (using getFeaturesAndOptions())
 */
/*export default (selectedFeatures, allLabels, claimOptions = {}) => {
  const features = selectedFeatures || {}
  const labels = allLabels && allLabels.hasOwnProperty('features') ? allLabels.features : {}
  const showClaimNumbers = claimOptions.hasOwnProperty('showClaimNumbers') && claimOptions.showClaimNumbers === true

  return defaultFeatureSettings.reduce((acc, item) => {
    let newItem = {
      ...item
    }

    if (newItem.hasOwnProperty('feature') && features.hasOwnProperty(newItem.feature) && features[newItem.feature]) {

      if(newItem.feature === 'reportClaim' && showClaimNumbers === true) {
        newItem.url = '/claims'
      }

      if(labels.hasOwnProperty(newItem.feature)) {
        const labelItem = labels[newItem.feature]

        if(labelItem.hasOwnProperty('label')) {
          newItem.name = labelItem.label
        }
        if(labelItem.hasOwnProperty('sublabel')) {
          newItem.description = labelItem.sublabel
        }

        if (!isEmpty(labelItem.url)) {
          newItem.url = labelItem.url
          newItem.isExternalUrl = true
        }
      }

      acc.push(newItem)
    }

    return acc
  }, [])
}*/

/**
 * Clean and sort features.
 *
 * @param {array} features
 * @return {array}
 */
const sortAvailableFeatures = (features) => {
  try {
    return features
      // Filter out empty features
      .filter(feature => feature !== null)
      .sort((a, b) => {
        if (!(a.hasOwnProperty('order') && b.hasOwnProperty('order'))) {
          return 0
        }
        // ASC sort
        return a.order - b.order
      })
  } catch (e) {
    return features
  }
}

/**
 * Format features received from PW API
 *
 * // TODO: PAT-3298 Deprecate later when it's cleared from all device's cache/pre-cache,
 *   because backend should dictate which features show up on kiosk...
 */
export function getFeaturesLegacy(features, labels, claimOptions = {}) {
  const defaultResponse = []

  try {
    // Exit if we don't have any feature to process (e.g. we didn't receive any from PW API)
    if (isEmpty(features)) {
      return defaultResponse
    }

    // Take additional feature settings (currently from "labels") from backend
    const featuresSettings = labels && labels.hasOwnProperty('features') ? labels.features : {}
    const showClaimNumbers = claimOptions.hasOwnProperty('showClaimNumbers') && claimOptions.showClaimNumbers === true

    const combinedFeatures = Object.keys(features).map((featureKey) => {
      // Exit if Chat
      if (featureKey === 'chat') {
        return null
      }

      // Exit if feature is not enabled
      if (!(features.hasOwnProperty(featureKey) && features[featureKey] === true)) {
        return null
      }

      // Exit if we didn't get additional settings from backend
      if (!(featuresSettings.hasOwnProperty(featureKey) && !isEmpty(featuresSettings[featureKey]))) {
        return null
      }

      // Take additional settings from backend
      const featureSettings = featuresSettings[featureKey]

      const defaultSettings = defaultFeatureSettings.find(f => f.feature === featureKey)
      let newItem = !isEmpty(defaultSettings) ? defaultSettings : {}

      if(newItem.hasOwnProperty('feature') && newItem.feature === 'reportClaim' && showClaimNumbers === true) {
        newItem.url = '/claims'
      }

      if (featureSettings.hasOwnProperty('label')) {
        newItem.name = featureSettings.label
      }
      if (featureSettings.hasOwnProperty('sublabel')) {
        newItem.description = featureSettings.sublabel
      }

      if (!isEmpty(featureSettings.url)) {
        newItem.url = featureSettings.url
        newItem.isExternalUrl = true
      }

      if (isEmpty(newItem)) {
        return null
      }

      return newItem
    })

    return sortAvailableFeatures(combinedFeatures)
  } catch (e) {
    logError('Get features', e)
    return defaultResponse
  }
}

/**
 * Format features and options received from backend (`/api/kiosk/brand/getKioskBrandSettings`).
 *
 * Also adds Kiosk icons and URLs, since backend doesn't know those.
 *
 * @param {object} settings Received from backend
 * @return {[]}
 */
export function getFeaturesAndOptions(settings) {
  const defaultResponse = []
  try {
    // If we didn't receive features in new format
    if (!(
      settings.hasOwnProperty('featuresAndOptions')
      && !isEmpty(settings.featuresAndOptions)
      && Array.isArray(settings.featuresAndOptions))
    ) {
      // Fallback to getting features from old API response format,
      // this could probably be removed (replace with "return defaultResponse")
      // if we remove getting features, labels, claimOptions, etc. separately
      return getFeaturesLegacy(settings.features, settings.labels, settings.claimOptions)
    }

    const showClaimNumbers = !!(
      settings.hasOwnProperty('claimOptions')
      && settings.claimOptions.hasOwnProperty('showClaimNumbers')
      && settings.claimOptions.showClaimNumbers === true
    )

    const combinedFeatures = settings.featuresAndOptions.map(feature => {
      const featureKey = feature.hasOwnProperty('feature') && feature.feature ? feature.feature : ''
      // Exit if Chat
      if (featureKey === 'chat') {
        return null
      }

      // Exit if name (main label) is not defined
      if (!(feature.hasOwnProperty('name') && feature.name.length > 0)) {
        return null
      }

      // Merge default and custom properties from backend
      let newItem = {
        ...defaultFeatureProperties,
        ...feature,
      }

      // Set custom icons and URLs for known features (backend doesn't know these)
      if(featureKey === 'requestQuote') {
        newItem.leftIcon = 'GetQuote'
        newItem.url = '/quote'
      }
      if(featureKey === 'documents') {
        newItem.leftIcon = 'Docs'
        newItem.url = '/documents'
      }
      if(featureKey === 'reportClaim') {
        newItem.leftIcon = 'Claim'
        if (showClaimNumbers === true) {
          newItem.url = '/claims'
        } else {
          newItem.url = '/claims/form'
        }
      }
      if(featureKey === 'payments') {
        newItem.leftIcon = 'Payment'
        newItem.url = '/payments'
      }
      if(featureKey === 'inventory') {
        newItem.leftIcon = 'Inventory'
        newItem.url = '/inventory'
      }
      if(featureKey === 'services') {
        newItem.leftIcon = 'VehicleGear'
        newItem.url = '/services'
      }

      if (feature.hasOwnProperty('externalUrl') && !isEmpty(feature.externalUrl)) {
        newItem.url = feature.externalUrl
        newItem.isExternalUrl = true
      }

      if (feature.hasOwnProperty('type') && feature.type === 'customPage') {
        // newItem.url = '/cp' + (feature.hasOwnProperty('requestPath') && feature.requestPath ? '#' + feature.requestPath : '')
        newItem.url = '/cp'
      }

      return newItem
    })

    return sortAvailableFeatures(combinedFeatures)

  } catch (e) {
    return defaultResponse
  }
}
